/** Header/footer pieces shared by sign-in and the workspace chooser. */
import { CircleHelp, Globe, Moon, Sun } from '../ui/glyphs.tsx';
import { useI18n, type Locale } from '../lib/i18n.tsx';
import type { Prefs } from '../lib/prefs.ts';
import { Dropdown } from '../ui/ui.tsx';

const env = import.meta.env as Record<string, string | undefined>;
/** Public URLs come from deployment config; unset links render as plain text, never as dead links. */
export const LINKS = { help: env.VITE_HELP_URL, terms: env.VITE_TERMS_URL, privacy: env.VITE_PRIVACY_URL };

export const LANGUAGES: { value: Locale; label: string }[] = [
  { value: 'en', label: 'English' }, { value: 'ru', label: 'Русский' }, { value: 'ko', label: '한국어' },
];

function MaybeLink({ href, children, className }: { href?: string; children: React.ReactNode; className?: string }) {
  return href ? <a href={href} target="_blank" rel="noreferrer" className={className}>{children}</a> : <span className={className} style={{ color: 'var(--accent)' }}>{children}</span>;
}

export function HelpLink() {
  const { t } = useI18n();
  return <MaybeLink href={LINKS.help} className="help-link"><CircleHelp size={26} strokeWidth={1.6} />{t('auth.help')}</MaybeLink>;
}

export function LegalLinks() {
  const { t } = useI18n();
  return (
    <span className="legal">
      <MaybeLink href={LINKS.terms}>{t('auth.terms')}</MaybeLink>
      <span className="dot" aria-hidden />
      <MaybeLink href={LINKS.privacy}>{t('auth.privacy')}</MaybeLink>
    </span>
  );
}

/** Language and light/dark quick toggle. The full System option lives in Settings → Appearance. */
export function PrefsCluster({ prefs, onPrefs }: { prefs: Prefs; onPrefs: (p: Prefs) => void }) {
  const { t } = useI18n();
  const dark = prefs.theme === 'dark' || (prefs.theme === 'system' && matchMedia('(prefers-color-scheme: dark)').matches);
  return (
    <div className="prefs-cluster">
      <Dropdown className="lang" label="Language" value={prefs.locale} options={LANGUAGES} onChange={locale => onPrefs({ ...prefs, locale })}
        icon={<Globe size={22} strokeWidth={1.6} className="muted" />} />
      <span className="divider" aria-hidden />
      <div className="theme-toggle" role="radiogroup" aria-label={t('settings.theme')}>
        <button type="button" role="radio" aria-checked={!dark} aria-label={t('theme.light')} className={!dark ? 'on' : ''} onClick={() => onPrefs({ ...prefs, theme: 'light' })}><Sun size={19} /></button>
        <button type="button" role="radio" aria-checked={dark} aria-label={t('theme.dark')} className={dark ? 'on' : ''} onClick={() => onPrefs({ ...prefs, theme: 'dark' })}><Moon size={19} /></button>
      </div>
    </div>
  );
}
